/**
 * @module game/Fruit
 * Fruit entity — spawned, moved via parabolic physics, and destroyed on slash.
 *
 * Two visual states per fruit type:
 *  - `whole`    — the sprite shown while the fruit is live.
 *  - `splitting` — two half-sprites with outward velocity applied on slash.
 *  - `offscreen` — entity is past the canvas bottom and should be removed.
 *
 * Sprite management is PixiJS-agnostic: this module accepts a generic
 * Container-like interface so tests can use a lightweight stub.
 */

import type { Fruit, Vec2 } from '../types.js';
import { CONFIG } from '../game-config.js';
import type { FruitType } from '../game-config.js';
import { stepBody, isOffscreen } from './physics.js';

export type { FruitType };

// ---------------------------------------------------------------------------
// Internal state
// ---------------------------------------------------------------------------

let _nextId = 0;

// Split-half velocity scale perpendicular to slash direction (px/s fraction).
const SPLIT_PERP_SPEED = 220;
// How long before split halves are also considered offscreen (ms).
const SPLIT_OFFSCREEN_EXTRA_MS = 800;

// ---------------------------------------------------------------------------
// Minimal PixiJS-compatible interfaces (avoids importing pixi.js in tests)
// ---------------------------------------------------------------------------

/** Minimum interface required from a PixiJS Sprite (or stub). */
interface SpriteRef {
  x: number;
  y: number;
  visible: boolean;
  destroy?(): void;
}

/** Minimum interface required from a PixiJS Container. */
interface ContainerRef {
  addChild(child: SpriteRef): void;
  removeChild(child: SpriteRef): void;
}

// ---------------------------------------------------------------------------
// Internal split-half state attached to sliced fruits
// ---------------------------------------------------------------------------

interface SplitHalves {
  halfA: SpriteRef;
  halfB: SpriteRef;
  /** Half-A velocity (px/s) after split. */
  vxA: number;
  vyA: number;
  /** Half-B velocity (px/s) after split. */
  vxB: number;
  vyB: number;
  splitTime: number; // performance.now() when split occurred
}

/** Extended fruit with internal rendering state. */
interface FruitInternal extends Fruit {
  _sprite: SpriteRef | null;
  _split: SplitHalves | null;
}

// ---------------------------------------------------------------------------
// Public functional API
// ---------------------------------------------------------------------------

/**
 * Create a Fruit entity and add its whole sprite to the given container.
 * isCritical defaults to false; caller (Spawner) decides based on CONFIG.fruit.criticalChance.
 *
 * @param type       - Fruit type key from CONFIG.fruits.
 * @param position   - Initial canvas position (px).
 * @param velocity   - Initial velocity vector (px/s).
 * @param container  - PixiJS Container to add the sprite to.
 * @param isCritical - Whether this is the rare critical (golden) variant.
 */
export function createFruit(
  type: FruitType,
  position: Vec2,
  velocity: Vec2,
  container: ContainerRef,
  isCritical = false,
): Fruit {
  const fruitCfg = CONFIG.fruits[type];
  const hitRadius = fruitCfg?.hitRadius ?? CONFIG.fruit.hitRadius;

  // Stub sprite: real implementation will call getTexture(type + '-whole').
  // Placing a typed placeholder means PixiJS-free tests work without mocking.
  const sprite: SpriteRef = {
    x: position.x,
    y: position.y,
    visible: true,
  };

  container.addChild(sprite);

  const fruit: FruitInternal = {
    id: _nextId++,
    type,
    state: 'whole',
    x: position.x,
    y: position.y,
    vx: velocity.x,
    vy: velocity.y,
    hitRadius,
    isCritical,
    sprite,
    _sprite: sprite,
    _split: null,
  };

  return fruit;
}

/**
 * Update fruit physics for one render frame.
 *
 * @param fruit       - The fruit entity to update.
 * @param dt          - Frame delta in seconds, scaled by current timeScale.
 * @param canvasWidth  - Canvas width (px) — used to check horizontal bounds.
 * @param canvasHeight - Canvas height (px) — used to check offscreen.
 * @returns true if the fruit has exited the screen bounds and should be removed.
 */
export function updateFruit(
  fruit: Fruit,
  dt: number,
  _canvasWidth: number,
  canvasHeight: number,
): boolean {
  const f = fruit as FruitInternal;

  if (f.state === 'offscreen') return true;

  if (f.state === 'splitting') {
    // Move each half independently.
    if (f._split !== null) {
      f._split.vxA += 0; // gravity already applied via stepBody on the main body
      f._split.vyA += CONFIG.fruit.gravity * dt;
      f._split.vxB += 0;
      f._split.vyB += CONFIG.fruit.gravity * dt;

      const halfA = f._split.halfA;
      const halfB = f._split.halfB;

      halfA.x += f._split.vxA * dt;
      halfA.y += f._split.vyA * dt;
      halfB.x += f._split.vxB * dt;
      halfB.y += f._split.vyB * dt;

      // Check if halves are past the canvas bottom.
      const splitAge = performance.now() - f._split.splitTime;
      if (
        splitAge > SPLIT_OFFSCREEN_EXTRA_MS &&
        halfA.y > canvasHeight + 80 &&
        halfB.y > canvasHeight + 80
      ) {
        f.state = 'offscreen';
        return true;
      }
    }
    return false;
  }

  // Normal whole-fruit physics.
  stepBody(f, dt, CONFIG.fruit.gravity);

  if (f._sprite !== null) {
    f._sprite.x = f.x;
    f._sprite.y = f.y;
  }

  if (isOffscreen(f, canvasHeight)) {
    f.state = 'offscreen';
    return true;
  }

  return false;
}

/**
 * Trigger the slice animation: swap whole sprite for half-a + half-b sprites,
 * apply split velocity perpendicular to slashDir, schedule removal after animation.
 *
 * The actual sprite swap (getTexture calls) is stubbed here — the render layer
 * or AssetLoader integration will replace sprite.visible toggling with real textures.
 *
 * @param fruit    - The fruit entity to slice.
 * @param slashDir - Unit direction vector of the slash.
 * @param container - PixiJS Container for adding half sprites.
 */
export function sliceFruit(
  fruit: Fruit,
  slashDir: Vec2,
  container: ContainerRef,
): void {
  const f = fruit as FruitInternal;

  if (f.state !== 'whole') return;

  // Hide the whole sprite.
  if (f._sprite !== null) {
    f._sprite.visible = false;
  }

  // Perpendicular direction to slash (rotate 90°).
  const perpX = -slashDir.y;
  const perpY = slashDir.x;

  const halfA: SpriteRef = {
    x: f.x - perpX * 6,
    y: f.y - perpY * 6,
    visible: true,
  };
  const halfB: SpriteRef = {
    x: f.x + perpX * 6,
    y: f.y + perpY * 6,
    visible: true,
  };

  container.addChild(halfA);
  container.addChild(halfB);

  f._split = {
    halfA,
    halfB,
    vxA: f.vx + perpX * SPLIT_PERP_SPEED,
    vyA: f.vy - perpY * SPLIT_PERP_SPEED,
    vxB: f.vx - perpX * SPLIT_PERP_SPEED,
    vyB: f.vy + perpY * SPLIT_PERP_SPEED,
    splitTime: performance.now(),
  };

  f.state = 'splitting';
}

/**
 * Remove fruit sprite from scene and free resources.
 *
 * @param fruit     - The fruit entity to destroy.
 * @param container - PixiJS Container to remove sprites from.
 */
export function destroyFruit(fruit: Fruit, container: ContainerRef): void {
  const f = fruit as FruitInternal;

  if (f._sprite !== null) {
    container.removeChild(f._sprite);
    f._sprite.destroy?.();
    f._sprite = null;
  }

  if (f._split !== null) {
    container.removeChild(f._split.halfA);
    f._split.halfA.destroy?.();
    container.removeChild(f._split.halfB);
    f._split.halfB.destroy?.();
    f._split = null;
  }

  f.state = 'offscreen';
}
